import { Fragment } from 'react';
import Box from '@mui/material/Box';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router';

import { Checkbox } from 'common/components/Checkbox';
import { TableCell } from 'common/components/Table';
import { UserAvatar } from 'user/components/UserAvatar';
import {
    SkillReportMatrixCell,
    SkillReportMatrixCertificateBodyCell,
} from 'skill/components/SkillReportMatrix';
import {
    ISkillCertificateColumn,
    TUserCertificates,
} from 'skill/hooks/useSkillReportColumnExpansion';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import {
    IBasicSkillFragment,
    IBasicUserFragment,
    IPortfolioItemMatrixFragment,
} from 'graphql/types';

interface IProps {
    portfolioItems: IPortfolioItemMatrixFragment[];
    skills: IBasicSkillFragment[];
    user: IBasicUserFragment;
    expandedSkillIds: Set<string>;
    certificatesBySkill: Map<string, ISkillCertificateColumn[]>;
    userCertificates: TUserCertificates;
    loadingCertificates: boolean;
    isSelected?: boolean;
    onSelect?(): void;
    onUpdate?(): void;
}

export const SkillReportMatrixRow = ({
    portfolioItems,
    skills,
    user,
    expandedSkillIds,
    certificatesBySkill,
    userCertificates,
    loadingCertificates,
    isSelected,
    onSelect,
    onUpdate,
}: IProps) => {
    const theme = useTheme();
    const location = useLocation();
    const { openGlobalDrawer, closeGlobalDrawer } = useGlobalDrawer();

    const stickyOffsetLeft = {
        xs: 'auto',
        sm: theme.spacing(3),
    };

    const stickyOffset = {
        backgroundColor: 'common.white',
        position: {
            xs: 'static',
            sm: 'sticky',
        },
        left: stickyOffsetLeft,
    };

    return (
        <TableRow>
            <TableCell>
                <Checkbox
                    checked={isSelected}
                    onClick={(e) => {
                        onSelect?.();

                        e.stopPropagation();
                    }}
                />
            </TableCell>

            <TableCell
                colSpan={0}
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                    openGlobalDrawer({
                        type: IGlobalDrawerType.UserProfile,
                        itemId: user.id,
                        props: {
                            onUpdate: () => {
                                onUpdate?.();
                            },
                            onArchive: () => {
                                onUpdate?.();

                                closeGlobalDrawer({
                                    type: IGlobalDrawerType.UserProfile,
                                });
                            },
                        },
                    });
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <UserAvatar bgColor="secondary" size={45} user={user} />

                    <Typography sx={{ ml: 2 }} variant="body2">
                        {user.name}
                    </Typography>
                </Box>
            </TableCell>

            {skills.map((skill) => {
                const portfolioItem = portfolioItems.find(
                    (item) => skill.id === item.relatedId
                );
                const isExpanded = expandedSkillIds.has(skill.id);
                const cachedCertificates =
                    certificatesBySkill.get(skill.id) || [];
                const isLoading = isExpanded && loadingCertificates;
                // While the per-user query is in flight, treat the cached
                // canonical list as empty so only one placeholder slot
                // renders (with a spinner) — matching the initial-expand UX
                // where a single loader column slides in before the full
                // set of cert columns appears.
                const certificates = isLoading ? [] : cachedCertificates;
                const showFirstSlot = isExpanded || certificates.length > 0;
                const additionalCerts = certificates.slice(1);
                const skillCertificates = userCertificates.get(skill.id);

                return (
                    <Fragment key={skill.id}>
                        <SkillReportMatrixCell
                            align="center"
                            portfolioItem={portfolioItem}
                            skill={skill}
                            sx={{
                                ...stickyOffset,
                                borderLeftStyle: 'solid',
                                borderLeftWidth: isExpanded ? '1px' : 0,
                                borderLeftColor: 'divider',
                                transition: 'border-left-width 0.3s ease',
                            }}
                            userId={user.id}
                            userName={user.name}
                            onUpdate={(portfolioItemId) => {
                                onUpdate?.();

                                if (portfolioItemId) {
                                    openGlobalDrawer(
                                        {
                                            type: IGlobalDrawerType.PortfolioItem,
                                            itemId: portfolioItemId,
                                        },
                                        location
                                    );
                                }
                            }}
                        />

                        {showFirstSlot && (
                            <SkillReportMatrixCertificateBodyCell
                                certificate={certificates[0]}
                                child={
                                    certificates[0]
                                        ? skillCertificates?.get(
                                              certificates[0].id
                                          )
                                        : undefined
                                }
                                columnIndex={0}
                                isExpanded={isExpanded}
                                isLast={additionalCerts.length === 0}
                                isLoading={isLoading}
                                key={`first-${skill.id}`}
                                skill={skill}
                                user={user}
                                onUpdate={onUpdate}
                            />
                        )}

                        {additionalCerts.map((certificate, index) => (
                            <SkillReportMatrixCertificateBodyCell
                                certificate={certificate}
                                child={skillCertificates?.get(certificate.id)}
                                columnIndex={index + 1}
                                isExpanded={isExpanded}
                                isLast={index === additionalCerts.length - 1}
                                key={certificate.id}
                                skill={skill}
                                user={user}
                                onUpdate={onUpdate}
                            />
                        ))}
                    </Fragment>
                );
            })}
        </TableRow>
    );
};
